import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  TablePagination,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Settings as StatusIcon,
  FilterList as FilterIcon,
  GetApp as ExportIcon,
  PictureAsPdf as PdfIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  DonutLargeOutlined,
  Compare as CompareIcon,
  RuleFolderSharp,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { set } from "react-hook-form";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { getSubmissionTimestamp } from "../../utils/submissionUtils";

interface Submission {
  id: number;
  uniqueId: string;
  status: string;
  paymentMethod: string;
  createdAt: string;
  adminNotes?: string;
  paymentInfo: {
    bkashPaymentId?: string;
    amount?: number;
    status?: string;
    receiptPath?: string;
  };
  form: {
    id: number;
    title: string;
    fields?: Array<{
      id: string;
      name: string;
      label: string;
      type: string;
      required: boolean;
      displayInAdminTable?: boolean; // Whether this field should be displayed as a column
    }>;
    creator?: {
      username: string;
      email: string;
    };
  };
  data: any;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`submissions-tabpanel-${index}`}
      aria-labelledby={`submissions-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const AdminSubmissions: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSnackbar } = useSnackbar();

  // Initialize filters from URL parameters
  const initialFilters = {
    formId: searchParams.get('formId') || "",
    status: searchParams.get('status') || "",
    paymentMethod: searchParams.get('paymentMethod') || "",
    dateFrom: searchParams.get('dateFrom') || "",
    dateTo: searchParams.get('dateTo') || "",
  };

  // State variables
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [totalSubmissions, setTotalSubmissions] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusDialog, setStatusDialog] = useState(false);
  const [selectedSubmission, setSelectedSubmission] =
    useState<Submission | null>(null);
  const [newStatus, setNewStatus] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [processing, setProcessing] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const [forms, setForms] = useState<Array<{ id: number; title: string }>>([]);
  const [receiptModal, setReceiptModal] = useState<{
    open: boolean;
    receiptPath: string;
    fileType: "image" | "pdf";
    submission: Submission | null;
  }>({
    open: false,
    receiptPath: "",
    fileType: "image",
    submission: null,
  });

  // Duplicate detection state
  const [duplicateDialog, setDuplicateDialog] = useState<{
    open: boolean;
    submissions: Submission[];
    duplicateGroups: Submission[][];
  }>({
    open: false,
    submissions: [],
    duplicateGroups: [],
  });
  const [duplicateFieldsDialog, setDuplicateFieldsDialog] = useState<{
    open: boolean;
    selectedFields: string[];
    availableFields: Array<{ id: string; label: string; formId: number; formTitle: string }>;
  }>({
    open: false,
    selectedFields: [],
    availableFields: [],
  });
  const [showDuplicateHighlight, setShowDuplicateHighlight] = useState(false);
  const [currentDuplicateGroups, setCurrentDuplicateGroups] = useState<Submission[][]>([]);
  const [selectedSubmissions, setSelectedSubmissions] = useState<Submission[]>([]);
  const [compareDialog, setCompareDialog] = useState<{
    open: boolean;
    submissions: Submission[];
    fieldComparison: Array<{ fieldId: string; label: string; values: any[]; isDuplicate: boolean }>;
  }>({
    open: false,
    submissions: [],
    fieldComparison: [],
  });

  // Fetch functions
  const fetchForms = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/forms`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setForms(response.data.forms || []);
    } catch (error: any) {
      console.error("Error fetching forms:", error);
    }
  }, []);

  const fetchSubmissions = useCallback(
    async (customFilters?: any, customPage?: number, customRowsPerPage?: number) => {
      try {
        setLoading(true);
        const queryParams = new URLSearchParams();

        // Apply filters if provided
        const activeFilters = customFilters || filters;
        if (activeFilters.formId)
          queryParams.append("formId", activeFilters.formId);
        if (activeFilters.status)
          queryParams.append("status", activeFilters.status);
        if (activeFilters.paymentMethod)
          queryParams.append("paymentMethod", activeFilters.paymentMethod);
        if (activeFilters.dateFrom)
          queryParams.append("dateFrom", activeFilters.dateFrom);
        if (activeFilters.dateTo)
          queryParams.append("dateTo", activeFilters.dateTo);

        // Add pagination parameters
        const currentPage = customPage !== undefined ? customPage + 1 : page + 1; // Backend uses 1-based indexing
        const currentRowsPerPage = customRowsPerPage || rowsPerPage;
        queryParams.append("page", currentPage.toString());
        queryParams.append("limit", currentRowsPerPage.toString());

        const apiUrl = `${
          process.env.REACT_APP_API_URL
        }/api/submissions?${queryParams.toString()}`;

        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        
        setSubmissions(response.data.submissions || []);
        setTotalSubmissions(response.data.total || 0);
      } catch (error: any) {
        console.error("❌ Error fetching submissions:", error);
        setError("Failed to fetch submissions");
      } finally {
        setLoading(false);
      }
    },
    [filters, page, rowsPerPage]
  );

  useEffect(() => {
    fetchSubmissions();
    fetchForms();
  }, [fetchSubmissions, fetchForms]);

  // Filter and export functions
  const getFilteredSubmissions = () => {
    return submissions.filter((submission) => {
      if (filters.formId && submission.form?.id !== parseInt(filters.formId))
        return false;
      if (filters.status && submission.status !== filters.status) return false;
      if (
        filters.paymentMethod &&
        submission.paymentMethod !== filters.paymentMethod
      )
        return false;
      if (
        filters.dateFrom &&
        new Date(submission.createdAt) < new Date(filters.dateFrom)
      )
        return false;
      if (
        filters.dateTo &&
        new Date(submission.createdAt) > new Date(filters.dateTo)
      )
        return false;
      return true;
    });
  };

  const handleFilterChange = (field: string, value: string) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    setPage(0); // Reset to first page when filtering
    
    // Update URL to reflect current filters
    const newSearchParams = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, val]) => {
      if (val) {
        newSearchParams.set(key, val);
      }
    });
    
    // Update URL without causing a page reload
    const newUrl = `${window.location.pathname}?${newSearchParams.toString()}`;
    window.history.replaceState({}, '', newSearchParams.toString() ? newUrl : window.location.pathname);
    
    fetchSubmissions(newFilters, 0, rowsPerPage);
  };

  // Duplicate detection functions
  const calculateSimilarity = (submission1: Submission, submission2: Submission): number => {
    if (!submission1.form?.fields || !submission2.form?.fields) return 0;
    if (submission1.form.id !== submission2.form.id) return 0; // Different forms
    
    const fields1 = submission1.data || {};
    const fields2 = submission2.data || {};
    
    let totalFields = 0;
    let matchingFields = 0;
    
    // Get all field keys from both submissions
    const allFieldKeys = new Set([...Object.keys(fields1), ...Object.keys(fields2)]);
    
    allFieldKeys.forEach(key => {
      // Skip system fields and empty values
      if (key.startsWith('_') || key === 'id' || key === 'createdAt' || key === 'updatedAt') return;
      
      totalFields++;
      const value1 = fields1[key];
      const value2 = fields2[key];
      
      // Normalize values for comparison
      const normalizeValue = (val: any): string => {
        if (val === null || val === undefined) return '';
        if (Array.isArray(val)) return val.join(',').toLowerCase().trim();
        return String(val).toLowerCase().trim();
      };
      
      const norm1 = normalizeValue(value1);
      const norm2 = normalizeValue(value2);
      
      if (norm1 === norm2 && norm1 !== '') {
        matchingFields++;
      }
    });
    
    return totalFields > 0 ? matchingFields / totalFields : 0;
  };

  const calculateFieldSpecificSimilarity = (
    submission1: Submission, 
    submission2: Submission, 
    selectedFields: string[]
  ): number => {
    if (!submission1.form?.fields || !submission2.form?.fields) return 0;
    if (submission1.form.id !== submission2.form.id) return 0; // Different forms
    if (selectedFields.length === 0) return 0;
    
    const fields1 = submission1.data || {};
    const fields2 = submission2.data || {};
    
    let totalFields = 0;
    let matchingFields = 0;
    
    selectedFields.forEach(fieldId => {
      // Try multiple possible keys to find the field value
      const possibleKeys = [
        fieldId,
        `field_${fieldId}`,
        // Also try to find by field name if available
        ...(submission1.form?.fields?.find(f => f.id === fieldId)?.name ? 
           [submission1.form.fields.find(f => f.id === fieldId)!.name] : [])
      ];
      
      let value1 = null;
      let value2 = null;
      
      for (const key of possibleKeys) {
        if (fields1.hasOwnProperty(key)) {
          value1 = fields1[key];
          break;
        }
      }
      
      for (const key of possibleKeys) {
        if (fields2.hasOwnProperty(key)) {
          value2 = fields2[key];
          break;
        }
      }
      
      // Only count fields that exist in both submissions
      if (value1 !== null && value2 !== null) {
        totalFields++;
        
        // Normalize values for comparison
        const normalizeValue = (val: any): string => {
          if (val === null || val === undefined) return '';
          if (Array.isArray(val)) return val.join(',').toLowerCase().trim();
          return String(val).toLowerCase().trim();
        };
        
        const norm1 = normalizeValue(value1);
        const norm2 = normalizeValue(value2);
        
        if (norm1 === norm2 && norm1 !== '') {
          matchingFields++;
        }
      }
    });
    
    return totalFields > 0 ? matchingFields / totalFields : 0;
  };

  const detectDuplicates = (submissionsList: Submission[], threshold: number = 0.8): Submission[][] => {
    const duplicateGroups: Submission[][] = [];
    const processed = new Set<number>();
    
    submissionsList.forEach((submission, index) => {
      if (processed.has(submission.id)) return;
      
      const similarSubmissions = [submission];
      processed.add(submission.id);
      
      submissionsList.forEach((otherSubmission, otherIndex) => {
        if (index !== otherIndex && !processed.has(otherSubmission.id)) {
          const similarity = calculateSimilarity(submission, otherSubmission);
          
          if (similarity >= threshold) {
            similarSubmissions.push(otherSubmission);
            processed.add(otherSubmission.id);
          }
        }
      });
      
      // Only add groups with more than one submission
      if (similarSubmissions.length > 1) {
        duplicateGroups.push(similarSubmissions);
      }
    });
    
    return duplicateGroups;
  };

  const detectFieldSpecificDuplicates = (
    submissionsList: Submission[], 
    selectedFields: string[]
  ): Submission[][] => {
    if (selectedFields.length === 0) return [];
    
    const duplicateGroups: Submission[][] = [];
    const processed = new Set<number>();
    
    submissionsList.forEach((submission, index) => {
      if (processed.has(submission.id)) return;
      
      const similarSubmissions = [submission];
      processed.add(submission.id);
      
      submissionsList.forEach((otherSubmission, otherIndex) => {
        if (index !== otherIndex && !processed.has(otherSubmission.id)) {
          const similarity = calculateFieldSpecificSimilarity(submission, otherSubmission, selectedFields);
          
          // For field-specific comparison, we want 100% match on selected fields
          if (similarity === 1.0) {
            similarSubmissions.push(otherSubmission);
            processed.add(otherSubmission.id);
          }
        }
      });
      
      // Only add groups with more than one submission
      if (similarSubmissions.length > 1) {
        duplicateGroups.push(similarSubmissions);
      }
    });
    
    return duplicateGroups;
  };

  const getAvailableFieldsForDuplicateDetection = (): Array<{ id: string; label: string; formId: number; formTitle: string }> => {
    const fieldsMap = new Map<string, { id: string; label: string; formId: number; formTitle: string }>();
    
    submissions.forEach(submission => {
      if (submission.form?.fields) {
        submission.form.fields.forEach(field => {
          // Only include fields that are suitable for duplicate detection
          if (!['html', 'heading', 'paragraph', 'divider', 'file'].includes(field.type)) {
            const key = `${field.id}_${submission.form!.id}`;
            if (!fieldsMap.has(key)) {
              fieldsMap.set(key, {
                id: field.id,
                label: field.label || field.name,
                formId: submission.form!.id,
                formTitle: submission.form!.title
              });
            }
          }
        });
      }
    });
    
    return Array.from(fieldsMap.values()).sort((a, b) => {
      if (a.formTitle !== b.formTitle) {
        return a.formTitle.localeCompare(b.formTitle);
      }
      return a.label.localeCompare(b.label);
    });
  };

  const getDuplicateGroups = (): Submission[][] => {
    const filteredSubmissions = getFilteredSubmissions();
    
    // If specific fields are selected for duplicate detection, use field-specific detection
    if (duplicateFieldsDialog.selectedFields.length > 0) {
      return detectFieldSpecificDuplicates(filteredSubmissions, duplicateFieldsDialog.selectedFields);
    }
    
    // Otherwise use the general percentage-based detection
    return detectDuplicates(filteredSubmissions);
  };

  const handleCompareDuplicates = () => {
    const duplicateGroups = getDuplicateGroups();
    setDuplicateDialog({
      open: true,
      submissions: [],
      duplicateGroups
    });
  };

  const handleCompareDuplicateAll = () => {
    // Always use percentage-based detection for "Compare Duplicate All"
    const filteredSubmissions = getFilteredSubmissions();
    const duplicateGroups = detectDuplicates(filteredSubmissions, 0.8);
    setDuplicateDialog({
      open: true,
      submissions: [],
      duplicateGroups
    });
  };

  const handleSelectDuplicateFields = () => {
    const availableFields = getAvailableFieldsForDuplicateDetection();
    setDuplicateFieldsDialog({
      open: true,
      selectedFields: duplicateFieldsDialog.selectedFields,
      availableFields
    });
  };

  const handleDuplicateFieldsChange = (fieldId: string, checked: boolean) => {
    setDuplicateFieldsDialog(prev => ({
      ...prev,
      selectedFields: checked 
        ? [...prev.selectedFields, fieldId]
        : prev.selectedFields.filter(id => id !== fieldId)
    }));
  };

  const handleApplyDuplicateFields = () => {
    setDuplicateFieldsDialog(prev => ({ ...prev, open: false }));
  };

  const handleHighlightDuplicates = () => {
    const filteredSubmissions = getFilteredSubmissions();
    
    // Use field-specific detection if fields are selected, otherwise use percentage-based
    let duplicateGroups: Submission[][];
    if (duplicateFieldsDialog.selectedFields.length > 0) {
      duplicateGroups = detectFieldSpecificDuplicates(filteredSubmissions, duplicateFieldsDialog.selectedFields);
    } else {
      duplicateGroups = detectDuplicates(filteredSubmissions, 0.8);
    }
    
    setCurrentDuplicateGroups(duplicateGroups);
    setShowDuplicateHighlight(true);
  };

  const handleClearHighlight = () => {
    setShowDuplicateHighlight(false);
    setCurrentDuplicateGroups([]);
  };

  const handleToggleSubmissionSelection = (submission: Submission) => {
    setSelectedSubmissions(prev => {
      const isSelected = prev.some(s => s.id === submission.id);
      if (isSelected) {
        return prev.filter(s => s.id !== submission.id);
      } else {
        return [...prev, submission];
      }
    });
  };

  const handleCompareSelected = () => {
    if (selectedSubmissions.length < 2) return;

    // Get all unique fields from selected submissions
    const allFieldsMap = new Map<string, { label: string; type: string }>();
    
    selectedSubmissions.forEach(submission => {
      if (submission.form?.fields) {
        submission.form.fields.forEach(field => {
          if (!['html', 'heading', 'paragraph', 'divider'].includes(field.type)) {
            allFieldsMap.set(field.id, { 
              label: field.label || field.name, 
              type: field.type 
            });
          }
        });
      }
    });

    // Create field comparison data
    const fieldComparison = Array.from(allFieldsMap.entries()).map(([fieldId, fieldInfo]) => {
      const values = selectedSubmissions.map(submission => {
        // Try multiple possible keys to find the field value
        const possibleKeys = [
          fieldId,
          `field_${fieldId}`,
          ...(submission.form?.fields?.find(f => f.id === fieldId)?.name ? 
             [submission.form.fields.find(f => f.id === fieldId)!.name] : [])
        ];
        
        let value = null;
        for (const key of possibleKeys) {
          if (submission.data && submission.data.hasOwnProperty(key)) {
            value = submission.data[key];
            break;
          }
        }
        
        return value;
      });

      // Check if all values are the same (ignoring null/undefined/empty)
      const normalizedValues = values.map(val => {
        if (val === null || val === undefined || val === '') return '';
        if (Array.isArray(val)) return val.join(',').toLowerCase().trim();
        return String(val).toLowerCase().trim();
      });

      const nonEmptyValues = normalizedValues.filter(val => val !== '');
      const isDuplicate = nonEmptyValues.length > 1 && 
                         nonEmptyValues.every(val => val === nonEmptyValues[0]);

      return {
        fieldId,
        label: fieldInfo.label,
        values,
        isDuplicate
      };
    });

    setCompareDialog({
      open: true,
      submissions: selectedSubmissions,
      fieldComparison
    });
  };

  const handleClearSelection = () => {
    setSelectedSubmissions([]);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      formId: "",
      status: "",
      paymentMethod: "",
      dateFrom: "",
      dateTo: "",
    };
    setFilters(clearedFilters);
    setPage(0); // Reset to first page when clearing filters
    fetchSubmissions(clearedFilters, 0, rowsPerPage);
  };

  const handleExportData = () => {
    const dataToExport = getFilteredSubmissions().map((submission) => {
      // Helper function to clean phone numbers (remove + prefix)
      const cleanPhoneNumber = (phone: string): string => {
        if (typeof phone === 'string' && phone.startsWith('+88')) {
          return phone.substring(3);
        }
        if (typeof phone === 'string' && phone.startsWith('+')) {
          return phone.substring(1);
        }

        // Ensure it is treated as a string in CSV
        return "'" + phone + "";
      };

      // Base submission info with string index signature
      const exportRow: { [key: string]: string } = {
        ID: submission.uniqueId,
        "Registration ID": getSubmissionTimestamp(submission.uniqueId) || "",
        "Form Name": submission.form?.title || "Unknown",
        // "Submitted By": submission.form?.creator?.username || "N/A",
        // Email: submission.form?.creator?.email || "N/A",
        "Payment Method": submission.paymentMethod,
        "Payment Amount": submission.paymentInfo?.amount ? `${submission.paymentInfo.amount}` : "",
        "Payment Status": submission.status,
        "Admin Notes": submission.adminNotes || "",
        "Submission Date": new Date(submission.createdAt).toLocaleDateString(),
        Status: submission.status,
      };

      // Add form field data using unified field mapping (only current values)
      if (submission.form?.fields) {
        submission.form.fields.forEach((field) => {
          // Use the same field mapping logic as the frontend display
          const possibleKeys = [
            field.id,           // Prioritize field.id (our standard key)
            field.name,
            `field_${field.id}`,
            `field_${field.name}`
          ];
          
          let value = null;
          for (const key of possibleKeys) {
            if (key && submission.data.hasOwnProperty(key)) {
              value = submission.data[key];
              break;
            }
          }
          
          // Use field label as column header, fallback to field name
          const columnHeader = field.label || field.name || field.id;
          
          // Format value for CSV export
          if (Array.isArray(value)) {
            exportRow[columnHeader] = (value as string[]).join(', ');
          } else if (value !== null && value !== undefined) {
            let formattedValue = String(value);
            
            // Special handling for phone number fields - remove + prefix
            if (field.type === 'tel' || 
                field.name?.toLowerCase().includes('phone') || 
                field.label?.toLowerCase().includes('phone') ||
                field.name?.toLowerCase().includes('mobile') || 
                field.label?.toLowerCase().includes('mobile')) {
              formattedValue = cleanPhoneNumber(formattedValue);
            }
            // if field type is heading, paragraph, divider, html then skip
            if (['heading', 'paragraph', 'divider', 'html'].includes(field.type)) {
              formattedValue = '';
            }
            
            exportRow[columnHeader] = formattedValue;
          } else {
            exportRow[columnHeader] = '';
          }
        });
      }

      return exportRow;
    });

    // Convert to CSV
    const headers = Object.keys(dataToExport[0] || {});
    const csvContent = [
      headers.join(","),
      ...dataToExport.map((row) =>
        headers.map((header) => `"${row[header] || ""}"`).join(",")
      ),
    ].join("\n");

    // Download CSV
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `submissions_${new Date().toISOString().split("T")[0]}_${new Date().toISOString().split("T")[1]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPDF = async () => {
    try {
      setProcessing(true);

      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append("format", "pdf");

      if (filters.formId) queryParams.append("formId", filters.formId);
      if (filters.status) queryParams.append("status", filters.status);
      if (filters.paymentMethod)
        queryParams.append("paymentMethod", filters.paymentMethod);
      if (filters.dateFrom) queryParams.append("startDate", filters.dateFrom);
      if (filters.dateTo) queryParams.append("endDate", filters.dateTo);

      // Make API request to backend export endpoint
      const response = await axios.get(
        `${
          process.env.REACT_APP_API_URL
        }/api/export/submissions?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          responseType: "blob", // Important for file download
        }
      );

      // Create download link
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `submissions_${new Date().toISOString().split("T")[0]}_${new Date().toISOString().split("T")[1]}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error("Error exporting PDF:", error);
      setError("Failed to export PDF. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const handleExportPDFWithPayment = async () => {
    try {
      setProcessing(true);

      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append("format", "pdf");
      queryParams.append("includePaymentDetails", "true");

      if (filters.formId) queryParams.append("formId", filters.formId);
      if (filters.status) queryParams.append("status", filters.status);
      if (filters.paymentMethod)
        queryParams.append("paymentMethod", filters.paymentMethod);
      if (filters.dateFrom) queryParams.append("startDate", filters.dateFrom);
      if (filters.dateTo) queryParams.append("endDate", filters.dateTo);

      // Make API request to backend export endpoint
      const response = await axios.get(
        `${
          process.env.REACT_APP_API_URL
        }/api/export/submissions?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          responseType: "blob", // Important for file download
        }
      );

      // Create download link
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `submissions_with_payment_${new Date().toISOString().split("T")[0]}_${new Date().toISOString().split("T")[1]}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error("Error exporting PDF with payment details:", error);
      setError("Failed to export PDF with payment details. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  // Status update functions
  const handleUpdateStatus = async () => {
    if (!selectedSubmission) return;

    setProcessing(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/submissions/${selectedSubmission.uniqueId}/status`,
        {
          status: newStatus,
          adminNotes,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      // Refresh submissions
      await fetchSubmissions();
      setStatusDialog(false);
      setSelectedSubmission(null);
      setAdminNotes("");
    } catch (error: any) {
      console.error("Error updating status:", error);
      setError("Failed to update status");
    } finally {
      setProcessing(false);
    }
  };

  const openStatusDialog = (submission: Submission) => {
    setSelectedSubmission(submission);
    setNewStatus(submission.status);
    setAdminNotes(submission.adminNotes || "");
    setStatusDialog(true);
  };

  // Helper functions
  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusIcon = (paymentMethod: string) => {
    switch (paymentMethod) {
      case "stripe":
        return "💳";
      case "bkash":
        return "📱";
      case "bank_transfer":
        return "🏦";
      default:
        return "💰";
    }
  };

  // Server-side pagination - no need for client-side filtering
  // All filtering is now handled by the backend API

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0); // Reset pagination when changing tabs
    
    // Apply status and payment method filters based on tab
    let statusFilter = "";
    switch (newValue) {
      case 1: statusFilter = "pending"; break;
      case 2: statusFilter = "completed"; break;
      case 3: statusFilter = "failed"; break;
      case 4: statusFilter = "pending"; break; // Bank transfer pending
      default: statusFilter = ""; break; // All submissions
    }
    
    // Start with current filters but update status
    const newFilters = { 
      ...filters, 
      status: statusFilter
    };
    
    // Handle paymentMethod filter based on tab
    if (newValue === 4) {
      // Bank Transfer Pending: set paymentMethod to bank_transfer
      newFilters.paymentMethod = "bank_transfer";
    } else {
      // All other tabs: clear paymentMethod filter completely
      newFilters.paymentMethod = "";
    }
    
    setFilters(newFilters);
    fetchSubmissions(newFilters, 0, rowsPerPage);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchSubmissions(undefined, newPage, rowsPerPage); // Fetch data for new page
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchSubmissions(undefined, 0, newRowsPerPage); // Fetch data with new page size
  };

  const handleViewReceipt = (receiptPath: string, submission: Submission) => {
    const normalizedPath = receiptPath.replace(/\\/g, "/"); // Convert backslashes to forward slashes
    const fileExtension = normalizedPath.split(".").pop()?.toLowerCase();
    const fileType = fileExtension === "pdf" ? "pdf" : "image";

    setReceiptModal({
      open: true,
      receiptPath: normalizedPath,
      fileType,
      submission,
    });
  };

  const updateSubmissionPayment = async (
    uniqueId: string,
    paymentData: {
      paymentStatus: string;
      bkashTrxId: string | null;
      bkashPaymentId: string;
    }
  ) => {
    return await axios.put(
      `${process.env.REACT_APP_API_URL}/api/submissions/${uniqueId}/payment`,
      paymentData
    );
  };

  const checkBkashAgain = async (bkashPaymentId: string, submission: Submission) => {

    const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/bkash/status/${bkashPaymentId}`
          );

          console.log("response", response);

          // Handle payment execution result
          let trxID = null;
          let paymentSuccess = false;

          if(response.data.transactionStatus === "Completed" && response.data.trxID !== null) {
            paymentSuccess = true;
            trxID = response.data.trxID;
            

          const updatePayment = await updateSubmissionPayment(submission.uniqueId, {
                    paymentStatus: paymentSuccess ? "completed" : "failed",
                    bkashTrxId: trxID,
                    bkashPaymentId: bkashPaymentId,
          });
          if(updatePayment.status === 200){
            showSnackbar("Bkash payment verified and submission updated successfully.", 'success');
            fetchSubmissions();
            handleCloseReceiptModal();
          }
          else{
            showSnackbar("Failed to update submission with Bkash payment details. Please try again.", 'error');
            setError("Failed to update submission with Bkash payment details. Please try again.");
          }
      }
      else{
          showSnackbar("Bkash payment not completed yet. Please try again later.", 'error');
          setError("Bkash payment not completed yet. Please try again later.");
        }
  }

  const handleCloseReceiptModal = () => {
    setReceiptModal({
      open: false,
      receiptPath: "",
      fileType: "image",
      submission: null,
    });
  };

  const handleDownloadReceipt = () => {
    if (receiptModal.receiptPath) {
      const link = document.createElement("a");
      link.href = `${process.env.REACT_APP_API_URL}/${receiptModal.receiptPath}`;
      link.download = receiptModal.receiptPath.split("/").pop() || "receipt";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const renderSubmissionsTable = (submissionsList: Submission[]) => {
    // Server-side pagination: use submissions directly without slicing
    let displaySubmissions = submissionsList;
    
    // No automatic duplicate filtering - only when admin clicks Compare Duplicates

    // Get all unique forms and their display fields
    const formsWithDisplayFields = new Map<number, { title: string; displayFields: Array<{ id: string; label: string; type: string }> }>();
    
    displaySubmissions.forEach(submission => {
      if (submission.form && submission.form.fields) {
        const displayFields = submission.form.fields
          .filter(field => field.displayInAdminTable && !['html', 'heading', 'paragraph', 'divider'].includes(field.type))
          .map(field => ({ id: field.id, label: field.label, type: field.type }));
        
        if (!formsWithDisplayFields.has(submission.form.id)) {
          formsWithDisplayFields.set(submission.form.id, {
            title: submission.form.title,
            displayFields
          });
        }
      }
    });

    // Get all unique display fields across all forms for consistent column headers
    const allDisplayFields = new Map<string, { label: string; type: string }>();
    formsWithDisplayFields.forEach(form => {
      form.displayFields.forEach(field => {
        if (!allDisplayFields.has(field.id)) {
          allDisplayFields.set(field.id, { label: field.label, type: field.type });
        }
      });
    });

    // Helper function to format field values for display
    const formatFieldValue = (value: any, fieldType: string): string => {
      if (value === null || value === undefined || value === '') {
        return '-';
      }
      
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      
      if (fieldType === 'date' && value) {
        try {
          return new Date(value).toLocaleDateString();
        } catch {
          return String(value);
        }
      }
      
      return String(value).length > 50 ? String(value).substring(0, 47) + '...' : String(value);
    };

    // Helper function to get field value from submission data
    const getFieldValue = (submission: Submission, fieldId: string): any => {
      if (!submission.data) return null;
      
      // Try multiple possible keys to find the field value
      const possibleKeys = [
        fieldId,
        `field_${fieldId}`,
        // Also try to find by field name if available
        ...(submission.form?.fields?.find(f => f.id === fieldId)?.name ? [submission.form.fields.find(f => f.id === fieldId)!.name] : [])
      ];
      
      for (const key of possibleKeys) {
        if (submission.data.hasOwnProperty(key)) {
          return submission.data[key];
        }
      }
      
      return null;
    };

    return (
      <>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedSubmissions.length > 0 && selectedSubmissions.length < displaySubmissions.length}
                    checked={displaySubmissions.length > 0 && selectedSubmissions.length === displaySubmissions.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedSubmissions(displaySubmissions);
                      } else {
                        setSelectedSubmissions([]);
                      }
                    }}
                  />
                </TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Registration ID</TableCell>
                <TableCell>Form</TableCell>
                
                {/* Dynamic form field columns */}
                {Array.from(allDisplayFields.entries()).map(([fieldId, field]) => (
                  <TableCell key={fieldId} sx={{ minWidth: 120 }}>
                    {field.label}
                  </TableCell>
                ))}
                
                <TableCell>Payment Method</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Admin Notes</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displaySubmissions.map((submission) => {
                // Check if this submission is in a duplicate group (only when highlighting is enabled)
                const isInDuplicateGroup = showDuplicateHighlight && currentDuplicateGroups.some(group => 
                  group.some(s => s.id === submission.id)
                );
                
                const isSelected = selectedSubmissions.some(s => s.id === submission.id);
                
                return (
                <TableRow key={submission.id} sx={{ 
                  backgroundColor: isInDuplicateGroup ? 'warning.light' : (isSelected ? 'action.selected' : 'inherit'),
                  '&:hover': {
                    backgroundColor: isInDuplicateGroup ? 'warning.main' : 'action.hover'
                  }
                }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleToggleSubmissionSelection(submission)}
                    />
                  </TableCell>
                  <TableCell>
                    {submission.uniqueId}
                  </TableCell>
                  <TableCell>
                    {getSubmissionTimestamp(submission.uniqueId) || ""}
                  </TableCell>
                  <TableCell>
                    {submission.form?.title || "Unknown Form"}
                  </TableCell>
                  
                  {/* Dynamic form field values */}
                  {Array.from(allDisplayFields.entries()).map(([fieldId, field]) => {
                    const value = getFieldValue(submission, fieldId);
                    const formattedValue = formatFieldValue(value, field.type);
                    
                    return (
                      <TableCell 
                        key={fieldId} 
                        title={formattedValue.length > 47 ? String(value) : undefined}
                        sx={{ 
                          maxWidth: 200, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap' 
                        }}
                      >
                        {formattedValue}
                      </TableCell>
                    );
                  })}
                  
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <span>{getStatusIcon(submission.paymentMethod)}</span>
                      <span>{submission.paymentMethod}</span>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={submission.status}
                      color={getStatusColor(submission.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {submission.adminNotes ? (
                      <Box
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={submission.adminNotes}
                      >
                        {submission.adminNotes}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No notes
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(submission.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(`/admin/submissions/${submission.uniqueId}`)
                        }
                        title="View Details"
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => openStatusDialog(submission)}
                        title="Update Payment Status"
                      >
                        <StatusIcon />
                      </IconButton>
                      {submission.paymentInfo?.receiptPath && (
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleViewReceipt(
                              submission.paymentInfo.receiptPath!, submission
                            )
                          }
                          title="View Receipt"
                        >
                          <DonutLargeOutlined />
                        </IconButton>
                      )}
                      {
                        // submission payment method is bkash and status is pending
                        submission.paymentMethod === 'bkash' && submission.status === 'pending' && typeof submission.paymentInfo?.bkashPaymentId === 'string' ?
                        (
                          <IconButton
                          size="small"
                          onClick={() =>
                            submission.paymentInfo.bkashPaymentId
                              ? checkBkashAgain(submission.paymentInfo.bkashPaymentId, submission)
                              : undefined
                          }
                          title="Update Bkash"
                        >
                          <RuleFolderSharp />
                        </IconButton>
                        )
                        :
                        (<></>)
                      }
                    </Box>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalSubmissions}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 300, 400, 500]}
        />
      </>
    );
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Submissions Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards
      <Box display="flex" gap={2} mb={3}>
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography variant="h6" color="primary">
              {totalSubmissions}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total Submissions
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography variant="h6" color="warning.main">
              -
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Pending (Loading...)
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography variant="h6" color="success.main">
              -
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Completed (Loading...)
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography variant="h6" color="info.main">
              -
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Bank Transfer (Loading...)
            </Typography>
          </CardContent>
        </Card>
      </Box>
 */}
      {/* Filters and Export */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <FilterIcon />
            <Typography variant="h6">Filters & Export</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel>Form</InputLabel>
              <Select
                value={filters.formId}
                label="Form"
                onChange={(e) => handleFilterChange("formId", e.target.value)}
              >
                <MenuItem value="">All Forms</MenuItem>
                {forms.map((form) => (
                  <MenuItem key={form.id} value={form.id}>
                    {form.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={filters.paymentMethod}
                label="Payment Method"
                onChange={(e) =>
                  handleFilterChange("paymentMethod", e.target.value)
                }
              >
                <MenuItem value="">All Methods</MenuItem>
                <MenuItem value="stripe">Stripe</MenuItem>
                <MenuItem value="bkash">bKash</MenuItem>
                <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
              </Select>
            </FormControl>

            <TextField
              sx={{ minWidth: 150 }}
              size="small"
              label="From Date"
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              sx={{ minWidth: 150 }}
              size="small"
              label="To Date"
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange("dateTo", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFilters}
            >
              Clear
            </Button>

            <Button
              variant="contained"
              size="small"
              startIcon={<ExportIcon />}
              onClick={handleExportData}
              disabled={getFilteredSubmissions().length === 0}
            >
              Export CSV
            </Button>

            <Button
              variant="contained"
              size="small"
              color="secondary"
              startIcon={<PdfIcon />}
              onClick={handleExportPDF}
              disabled={getFilteredSubmissions().length === 0 || processing}
            >
              {processing ? "Generating..." : "Export PDF"}
            </Button>

            <Button
              variant="contained"
              size="small"
              color="success"
              startIcon={<PdfIcon />}
              onClick={handleExportPDFWithPayment}
              disabled={getFilteredSubmissions().length === 0 || processing}
              sx={{ ml: 1 }}
            >
              {processing ? "Generating..." : "Export PDF + Payment"}
            </Button>

            <Button
              variant="outlined"
              size="small"
              color="info"
              startIcon={<CompareIcon />}
              onClick={handleSelectDuplicateFields}
              sx={{ ml: 1 }}
            >
              Select Duplicate Fields
            </Button>

            <Button
              variant="outlined"
              size="small"
              color="warning"
              startIcon={<CompareIcon />}
              onClick={handleCompareDuplicates}
              disabled={getFilteredSubmissions().length === 0}
              sx={{ ml: 1 }}
            >
              Compare Duplicates
            </Button>

            <Button
              variant="outlined"
              size="small"
              color="secondary"
              startIcon={<CompareIcon />}
              onClick={handleCompareDuplicateAll}
              disabled={getFilteredSubmissions().length === 0}
              sx={{ ml: 1 }}
            >
              Compare Duplicate All (% based)
            </Button>

            <Button
              variant={showDuplicateHighlight ? "contained" : "outlined"}
              size="small"
              color="warning"
              onClick={showDuplicateHighlight ? handleClearHighlight : handleHighlightDuplicates}
              disabled={getFilteredSubmissions().length === 0}
              sx={{ ml: 1 }}
            >
              {showDuplicateHighlight ? "Clear Highlight" : "Highlight Duplicates"}
            </Button>

            {selectedSubmissions.length > 0 && (
              <>
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  startIcon={<CompareIcon />}
                  onClick={handleCompareSelected}
                  disabled={selectedSubmissions.length < 2}
                  sx={{ ml: 1 }}
                >
                  Compare Selected ({selectedSubmissions.length})
                </Button>
                
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleClearSelection}
                  sx={{ ml: 1 }}
                >
                  Clear Selection
                </Button>
              </>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* No automatic duplicate detection summary - only shows when admin clicks Compare Duplicates */}
      
      {/* Duplicate Highlight Summary */}
      {showDuplicateHighlight && currentDuplicateGroups.length > 0 && (
        <Alert 
          severity="warning" 
          sx={{ mb: 2 }}
          action={
            <Button 
              color="warning" 
              size="small" 
              onClick={handleClearHighlight}
            >
              Clear Highlight
            </Button>
          }
        >
          <Typography variant="body2">
            <strong>Duplicates Highlighted:</strong> Found {currentDuplicateGroups.length} groups 
            containing {currentDuplicateGroups.reduce((sum, group) => sum + group.length, 0)} submissions
            {duplicateFieldsDialog.selectedFields.length > 0 
              ? ` based on ${duplicateFieldsDialog.selectedFields.length} selected fields (exact match).`
              : ` based on similarity analysis (≥80% field match).`}
          </Typography>
        </Alert>
      )}

      {showDuplicateHighlight && currentDuplicateGroups.length === 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            No duplicate submissions found in the current filtered results
            {duplicateFieldsDialog.selectedFields.length > 0 
              ? ` using the ${duplicateFieldsDialog.selectedFields.length} selected fields.`
              : ` using similarity analysis.`}
          </Typography>
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ width: "100%" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All Submissions" />
          <Tab label="Pending" />
          <Tab label="Completed" />
          <Tab label="Failed" />
          <Tab label="Bank Transfer Pending" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {renderSubmissionsTable(submissions)}
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          {renderSubmissionsTable(submissions)}
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          {renderSubmissionsTable(submissions)}
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          {renderSubmissionsTable(submissions)}
        </TabPanel>
        <TabPanel value={tabValue} index={4}>
          {renderSubmissionsTable(submissions)}
        </TabPanel>
      </Paper>

      {/* Status Update Dialog */}
      <Dialog
        open={statusDialog}
        onClose={() => setStatusDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Submission Status</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={newStatus}
                label="Status"
                onChange={(e) => setNewStatus(e.target.value)}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Admin Notes"
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Add notes about this status change..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateStatus}
            disabled={processing}
          >
            {processing ? "Updating..." : "Update Status"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receipt Modal */}
      <Dialog
        open={receiptModal.open}
        onClose={handleCloseReceiptModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: "80vh",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Payment Receipt</Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadReceipt}
              sx={{ mr: 1 }}
            >
              Download
            </Button>
            <IconButton
              size="small"
              onClick={() => openStatusDialog(receiptModal.submission!)}
              title="Update Payment Status"
            >
              <StatusIcon />
            </IconButton>
            <IconButton onClick={handleCloseReceiptModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {receiptModal.fileType === "image" ? (
            <Box
              component="img"
              src={`${process.env.REACT_APP_API_URL}/${receiptModal.receiptPath}`}
              alt="Payment Receipt"
              sx={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                backgroundColor: "#f5f5f5",
              }}
              onError={(e) => {
                console.error("Error loading image:", e);
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <Box
              component="iframe"
              src={`${process.env.REACT_APP_API_URL}/${receiptModal.receiptPath}`}
              title="Payment Receipt PDF"
              sx={{
                width: "100%",
                height: "70vh",
                border: "none",
                backgroundColor: "#f5f5f5",
              }}
              onError={(e) => {
                console.error("Error loading PDF:", e);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Duplicate Comparison Dialog */}
      <Dialog
        open={duplicateDialog.open}
        onClose={() => setDuplicateDialog({ open: false, submissions: [], duplicateGroups: [] })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Duplicate Submissions Comparison
          <Typography variant="body2" color="textSecondary">
            Found {duplicateDialog.duplicateGroups.length} potential duplicate groups
            {duplicateFieldsDialog.selectedFields.length > 0 
              ? ` using ${duplicateFieldsDialog.selectedFields.length} selected fields (exact match)`
              : ` using similarity analysis (≥80% field match)`}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {duplicateDialog.duplicateGroups.length === 0 ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              No duplicate submissions found! All submissions appear to be unique
              {duplicateFieldsDialog.selectedFields.length > 0 
                ? ` based on the selected fields.`
                : ` based on similarity analysis.`}
            </Alert>
          ) : (
            <Box>
              {duplicateDialog.duplicateGroups.map((group, groupIndex) => (
                <Paper key={groupIndex} sx={{ p: 2, mb: 3 }} elevation={2}>
                  <Typography variant="h6" gutterBottom color="warning.main">
                    Duplicate Group #{groupIndex + 1} ({group.length} submissions)
                    {duplicateFieldsDialog.selectedFields.length > 0 && (
                      <Chip 
                        label={`${duplicateFieldsDialog.selectedFields.length} field exact match`} 
                        size="small" 
                        color="info" 
                        sx={{ ml: 1 }} 
                      />
                    )}
                  </Typography>
                  
                  <Box display="flex" gap={2} sx={{ overflowX: 'auto', pb: 1 }}>
                    {group.map((submission, subIndex) => (
                      <Paper 
                        key={submission.id} 
                        sx={{ 
                          minWidth: 300, 
                          p: 2, 
                          border: '2px solid',
                          borderColor: subIndex === 0 ? 'primary.main' : 'warning.main'
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          {subIndex === 0 ? 'Original' : `Duplicate #${subIndex}`}
                        </Typography>
                        
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          ID: {submission.uniqueId}
                        </Typography>
                        
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Submitted: {new Date(submission.createdAt).toLocaleString()}
                        </Typography>

                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Status: <Chip label={submission.status} size="small" />
                        </Typography>

                        {/* Show form field data - prioritize selected fields for comparison */}
                        {submission.form?.fields && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Form Data:
                            </Typography>
                            
                            {/* Show selected duplicate detection fields first if any */}
                            {duplicateFieldsDialog.selectedFields.length > 0 && (
                              <Box sx={{ mb: 2, p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                                <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                                  Selected Fields for Duplicate Detection:
                                </Typography>
                                {duplicateFieldsDialog.selectedFields.map(fieldId => {
                                  const field = submission.form?.fields?.find(f => f.id === fieldId);
                                  if (!field) return null;
                                  
                                  const value = submission.data?.[field.id] || submission.data?.[field.name] || '-';
                                  const displayValue = Array.isArray(value) ? value.join(', ') : String(value);
                                  
                                  return (
                                    <Typography key={fieldId} variant="caption" display="block">
                                      <strong>{field.label}:</strong> {displayValue.length > 50 ? displayValue.substring(0, 47) + '...' : displayValue}
                                    </Typography>
                                  );
                                })}
                              </Box>
                            )}
                            
                            {/* Show other fields */}
                            {submission.form.fields
                              .filter(field => 
                                !['html', 'heading', 'paragraph', 'divider'].includes(field.type) &&
                                !duplicateFieldsDialog.selectedFields.includes(field.id)
                              )
                              .slice(0, 5) // Show only first 5 other fields to keep it compact
                              .map(field => {
                                const value = submission.data?.[field.id] || submission.data?.[field.name] || '-';
                                const displayValue = Array.isArray(value) ? value.join(', ') : String(value);
                                
                                return (
                                  <Typography key={field.id} variant="caption" display="block">
                                    <strong>{field.label}:</strong> {displayValue.length > 50 ? displayValue.substring(0, 47) + '...' : displayValue}
                                  </Typography>
                                );
                              })}
                            {submission.form.fields.length > 5 + duplicateFieldsDialog.selectedFields.length && (
                              <Typography variant="caption" color="textSecondary">
                                ... and {submission.form.fields.length - 5 - duplicateFieldsDialog.selectedFields.length} more fields
                              </Typography>
                            )}
                          </Box>
                        )}

                        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigate(`/admin/submissions/${submission.uniqueId}`)}
                          >
                            View Details
                          </Button>
                        </Box>
                      </Paper>
                    ))}
                  </Box>

                  {/* Similarity score */}
                  <Box sx={{ mt: 2, p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                    <Typography variant="caption">
                      <strong>Similarity Analysis:</strong> 
                      {duplicateFieldsDialog.selectedFields.length > 0 ? (
                        ` These submissions have 100% matching data in the ${duplicateFieldsDialog.selectedFields.length} selected fields`
                      ) : (
                        ` These submissions have ${Math.round(calculateSimilarity(group[0], group[1]) * 100)}% matching field data overall`
                      )}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateDialog({ open: false, submissions: [], duplicateGroups: [] })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Field Selection Dialog */}
      <Dialog
        open={duplicateFieldsDialog.open}
        onClose={() => setDuplicateFieldsDialog(prev => ({ ...prev, open: false }))}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Select Fields for Duplicate Detection
          <Typography variant="body2" color="textSecondary">
            Choose which fields should be compared to identify duplicate submissions. 
            Selected fields must match exactly (100%) to be considered duplicates.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {duplicateFieldsDialog.availableFields.length === 0 ? (
              <Alert severity="info">
                No suitable fields found for duplicate detection. Make sure you have submissions with form fields.
              </Alert>
            ) : (
              <Box>
                {/* Group fields by form */}
                {(() => {
                  const fieldsByForm = duplicateFieldsDialog.availableFields.reduce((acc, field) => {
                    if (!acc[field.formId]) {
                      acc[field.formId] = {
                        formTitle: field.formTitle,
                        fields: []
                      };
                    }
                    acc[field.formId].fields.push(field);
                    return acc;
                  }, {} as Record<number, { formTitle: string; fields: typeof duplicateFieldsDialog.availableFields }>);

                  return Object.entries(fieldsByForm).map(([formId, { formTitle, fields }]) => (
                    <Box key={formId} sx={{ mb: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        {formTitle}
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <FormGroup>
                        {fields.map(field => (
                          <FormControlLabel
                            key={`${field.id}_${field.formId}`}
                            control={
                              <Checkbox
                                checked={duplicateFieldsDialog.selectedFields.includes(field.id)}
                                onChange={(e) => handleDuplicateFieldsChange(field.id, e.target.checked)}
                              />
                            }
                            label={field.label}
                          />
                        ))}
                      </FormGroup>
                    </Box>
                  ));
                })()}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateFieldsDialog(prev => ({ ...prev, open: false }))}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApplyDuplicateFields}
            disabled={duplicateFieldsDialog.selectedFields.length === 0}
          >
            Apply ({duplicateFieldsDialog.selectedFields.length} fields selected)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Selected Submissions Comparison Dialog */}
      <Dialog
        open={compareDialog.open}
        onClose={() => setCompareDialog({ open: false, submissions: [], fieldComparison: [] })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Compare Selected Submissions
          <Typography variant="body2" color="textSecondary">
            Comparing {compareDialog.submissions.length} selected submissions
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {compareDialog.fieldComparison.length === 0 ? (
              <Alert severity="info">
                No fields found to compare in the selected submissions.
              </Alert>
            ) : (
              <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Field</TableCell>
                      {compareDialog.submissions.map((submission, index) => (
                        <TableCell key={submission.id} sx={{ fontWeight: 'bold', minWidth: 200 }}>
                          <Box>
                            <Typography variant="subtitle2">
                              Submission #{index + 1}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              ID: {submission.uniqueId}
                            </Typography>
                            <br />
                            <Typography variant="caption" color="textSecondary">
                              {new Date(submission.createdAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </TableCell>
                      ))}
                      <TableCell sx={{ fontWeight: 'bold', width: 100 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {compareDialog.fieldComparison.map((fieldComp) => (
                      <TableRow 
                        key={fieldComp.fieldId}
                        sx={{ 
                          backgroundColor: fieldComp.isDuplicate ? 'error.light' : 'inherit'
                        }}
                      >
                        <TableCell sx={{ fontWeight: 'medium' }}>
                          {fieldComp.label}
                        </TableCell>
                        {fieldComp.values.map((value, index) => {
                          const displayValue = value === null || value === undefined || value === '' 
                            ? '-' 
                            : (Array.isArray(value) ? value.join(', ') : String(value));
                          
                          return (
                            <TableCell key={index}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  wordBreak: 'break-word',
                                  maxWidth: 200,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}
                                title={displayValue.length > 50 ? displayValue : undefined}
                              >
                                {displayValue.length > 50 ? displayValue.substring(0, 47) + '...' : displayValue}
                              </Typography>
                            </TableCell>
                          );
                        })}
                        <TableCell>
                          {fieldComp.isDuplicate ? (
                            <Chip label="Duplicate" color="error" size="small" />
                          ) : (
                            <Chip label="Different" color="success" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompareDialog({ open: false, submissions: [], fieldComparison: [] })}>
            Close
          </Button>
          <Button onClick={handleClearSelection} color="primary">
            Clear Selection
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminSubmissions;
